import { Form, redirect, useActionData, useNavigation } from "react-router";
import type { Route } from "./+types/login";
import { getSession, commitSession } from "~/lib/auth.server";
import { API_URL } from "~/lib/api.server";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Layers } from "lucide-react";

export async function action({ request }: Route.ActionArgs) {
  const formData = await request.formData();
  const email = formData.get("email") as string;
  const password = formData.get("password") as string;

  try {
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });

    if (!res.ok) {
      return { error: "Invalid credentials" };
    }

    const data = await res.json();
    const token = data.access_token;

    // Decode and check role
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (payload.role !== "admin") {
      return { error: "Admin access required" };
    }

    const session = await getSession(request);
    session.set("token", token);
    session.set("user", data.user || { email });

    return redirect("/", {
      headers: { "Set-Cookie": await commitSession(session) },
    });
  } catch {
    return { error: "Could not reach the API" };
  }
}

export default function Login({ actionData }: Route.ComponentProps) {
  const navigation = useNavigation();
  const isSubmitting = navigation.state === "submitting";

  return (
    <div className="min-h-screen flex items-center justify-center bg-app-bg">
      <div className="w-full max-w-sm">
        <div className="bg-surface border border-border rounded-2xl p-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-accent/20 flex items-center justify-center">
              <Layers size={20} className="text-accent" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-text-primary">Swing IQ</h1>
              <p className="text-xs text-text-muted font-mono uppercase tracking-wider">Admin Console</p>
            </div>
          </div>

          <Form method="post" className="space-y-4">
            {actionData?.error && (
              <div className="bg-bad/10 border border-bad/30 text-bad rounded-lg px-4 py-3 text-sm">
                {actionData.error}
              </div>
            )}
            <div>
              <Label className="block text-xs font-mono uppercase tracking-wider text-text-dim mb-1.5">Email</Label>
              <Input
                name="email"
                type="email"
                required
                placeholder="admin@example.com"
                autoComplete="email"
              />
            </div>
            <div>
              <Label className="block text-xs font-mono uppercase tracking-wider text-text-dim mb-1.5">Password</Label>
              <Input
                name="password"
                type="password"
                required
                autoComplete="current-password"
              />
            </div>
            <Button
              type="submit"
              variant="default"
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Signing in…" : "Sign in"}
            </Button>
          </Form>
        </div>
      </div>
    </div>
  );
}
